import axios from 'axios'
import toast from 'react-hot-toast'
import { API_BASE_URL, API_PREFIX, TOKEN_KEY, USER_KEY, TOAST_MESSAGES } from '@/utils/constants'
import { getUserFriendlyErrorMessage, getNetworkErrorMessage, getAuthErrorMessage, formatValidationErrors } from '@/utils/errorMessages'

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token refresh state
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Function to check if token is close to expiration (within 5 minutes)
const isTokenNearExpiration = (token) => {
  if (!token) return true

  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const exp = payload.exp * 1000 // Convert to milliseconds
    const now = Date.now()
    const fiveMinutes = 5 * 60 * 1000 // 5 minutes in milliseconds

    return (exp - now) < fiveMinutes
  } catch (error) {
    console.error('Error parsing token:', error)
    return true
  }
}

// Function to refresh token
const refreshToken = async () => {
  try {
    const currentToken = localStorage.getItem(TOKEN_KEY)
    if (!currentToken) {
      throw new Error('No token available')
    }

    const response = await axios.post(`${API_BASE_URL}${API_PREFIX}/auth/refresh-token`, {}, {
      headers: {
        'Authorization': `Bearer ${currentToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000
    })

    const { access_token, user } = response.data

    // Update stored token and user data
    localStorage.setItem(TOKEN_KEY, access_token)
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user))
    }

    return access_token
  } catch (error) {
    console.error('Token refresh failed:', error)
    // Clear invalid tokens
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    throw error
  }
}

// Request interceptor to add auth token and handle token refresh
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem(TOKEN_KEY)

    if (token) {
      // Check if token is near expiration and refresh if needed
      if (isTokenNearExpiration(token) && !config.url.includes('/auth/refresh-token')) {
        try {
          if (isRefreshing) {
            // If already refreshing, wait for it to complete
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject })
            }).then(token => {
              config.headers.Authorization = `Bearer ${token}`
              return config
            }).catch(err => {
              return Promise.reject(err)
            })
          }

          isRefreshing = true
          const newToken = await refreshToken()
          processQueue(null, newToken)
          config.headers.Authorization = `Bearer ${newToken}`
        } catch (error) {
          processQueue(error, null)
          // Don't reject here, let the request proceed with old token
          // The response interceptor will handle the 401 if it occurs
          config.headers.Authorization = `Bearer ${token}`
        } finally {
          isRefreshing = false
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    console.log('API Request:', config.method?.toUpperCase(), config.url, config.headers)
    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url, response.data)
    return response
  },
  async (error) => {
    console.error('API Error:', error.response?.status, error.response?.config?.url, error.response?.data)
    const { response, config } = error

    // Handle different error scenarios
    if (!response) {
      // Network error
      toast.error(getNetworkErrorMessage())
      return Promise.reject(error)
    }

    const { status, data } = response

    switch (status) {
      case 401:
        // Handle 401 Unauthorized errors
        if (config.url.includes('/auth/refresh-token')) {
          // Refresh token failed, clear auth data
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(USER_KEY)
          // Don't show toast here - user will be redirected to login
          break
        }

        // Try to refresh token once
        if (!config._retry) {
          config._retry = true

          try {
            if (isRefreshing) {
              // If already refreshing, wait for it to complete
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
              }).then(token => {
                config.headers.Authorization = `Bearer ${token}`
                return api(config)
              }).catch(err => {
                return Promise.reject(err)
              })
            }

            isRefreshing = true
            const newToken = await refreshToken()
            processQueue(null, newToken)

            // Retry the original request with new token
            config.headers.Authorization = `Bearer ${newToken}`
            return api(config)
          } catch (refreshError) {
            processQueue(refreshError, null)
            // Refresh failed, clear auth data
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem(USER_KEY)
            // Don't show toast here - user will be redirected to login
            return Promise.reject(error)
          } finally {
            isRefreshing = false
          }
        } else {
          // Already retried, clear auth data
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(USER_KEY)
          toast.error(getAuthErrorMessage())
        }
        break

      case 403:
        // Forbidden - use user-friendly message
        toast.error(getUserFriendlyErrorMessage(error, 'You don\'t have permission to perform this action.'))
        break

      case 404:
        // Not found - suppress toast for file-related endpoints as this is expected behavior
        if (!config.url.includes('/files/')) {
          toast.error(getUserFriendlyErrorMessage(error, 'The requested item could not be found.'))
        }
        break

      case 422:
        // Validation error - format validation errors nicely
        if (data?.detail) {
          if (typeof data.detail === 'string') {
            toast.error(getUserFriendlyErrorMessage(data.detail, 'Please check your input and try again.'))
          } else if (Array.isArray(data.detail)) {
            // Handle FastAPI validation error array
            toast.error(formatValidationErrors(data.detail))
          } else if (data.detail.message) {
            toast.error(getUserFriendlyErrorMessage(data.detail.message, 'Please check your input and try again.'))
          } else {
            toast.error('Please check your input and try again.')
          }
        } else if (data?.errors) {
          // Handle custom validation errors format
          toast.error(formatValidationErrors(data.errors))
        } else {
          toast.error('Please check your input and try again.')
        }
        break

      case 500:
        // Server error - use user-friendly message
        toast.error(getUserFriendlyErrorMessage(error, 'A system error occurred. Please try again later.'))
        break

      case 503:
        // Service unavailable
        toast.error('The service is temporarily unavailable. Please try again later.')
        break

      default:
        // Generic error - try to get user-friendly message
        const message = getUserFriendlyErrorMessage(error, 'Something went wrong. Please try again.')
        toast.error(message)
    }

    return Promise.reject(error)
  }
)

// API helper functions
export const apiRequest = {
  get: (url, config = {}) => api.get(url, config),
  post: (url, data = {}, config = {}) => api.post(url, data, config),
  put: (url, data = {}, config = {}) => api.put(url, data, config),
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),
}

// File upload helper
export const uploadFile = (url, file, onProgress = null) => {
  const formData = new FormData()
  formData.append('file', file)

  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        onProgress(percentCompleted)
      }
    },
  })
}

// Export the configured axios instance
export default api
